import { useState, useEffect } from 'react';
import { format } from 'date-fns';
import {
    News,
    CreateNewsDto,
    UpdateNewsDto,
    fetchAllNews,
    createNews,
    updateNews,
    deleteNews,
    setAuthToken,
} from '../api/news';

const COLORS = {
    primary: '#10b981',
    secondary: '#3b82f6',
    accent: '#8b5cf6',
    warning: '#f59e0b',
    success: '#22c55e',
    danger: '#ef4444',
};

// Token Modal Component
const TokenModal = ({ onSave, onClose }: { onSave: (token: string) => void; onClose: () => void }) => {
    const [token, setToken] = useState('');

    return (
        <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '500px', padding: '32px' }}>
                <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>🔐 Set JWT Token</h2>
                <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '24px' }}>Paste your JWT token from the main Kokki app to enable create/edit/delete operations.</p>
                <textarea
                    value={token}
                    onChange={(e) => setToken(e.target.value)}
                    placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                    style={{ width: '100%', padding: '12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', color: '#f1f5f9', fontSize: '14px', minHeight: '100px', resize: 'vertical', marginBottom: '20px' }}
                />
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button onClick={onClose} style={{ padding: '10px 20px', background: '#334155', color: '#f1f5f9', border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                    <button onClick={() => { setAuthToken(token); onSave(token); }} style={{ padding: '10px 20px', background: COLORS.primary, color: '#0f172a', border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>Save Token</button>
                </div>
            </div>
        </div>
    );
};

// News Form Modal
interface NewsFormModalProps {
    news?: News | null;
    onSave: (data: CreateNewsDto | UpdateNewsDto) => void;
    onClose: () => void;
    isLoading: boolean;
}

const NewsFormModal = ({ news, onSave, onClose, isLoading }: NewsFormModalProps) => {
    const [formData, setFormData] = useState<CreateNewsDto>({
        title: news?.title || '',
        description: news?.description || '',
        imageUrl: news?.imageUrl || '',
        buttonText: news?.buttonText || '',
        buttonColor: news?.buttonColor || '#10b981',
        navigationUrl: news?.navigationUrl || '',
        position: news?.position || 1,
        isActive: news?.isActive ?? true,
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        onSave(formData);
    };

    const inputStyle = { width: '100%', padding: '12px', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', color: '#f1f5f9', fontSize: '14px' };
    const labelStyle = { color: '#94a3b8', fontSize: '12px', marginBottom: '6px', display: 'block', fontWeight: '600' };

    return (
        <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
            <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
                <div style={{ padding: '24px', borderBottom: '1px solid #334155', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700' }}>{news ? '✏️ Edit News' : '➕ Create News'}</h2>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '28px', cursor: 'pointer' }}>×</button>
                </div>
                <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
                    <div style={{ display: 'grid', gap: '20px' }}>
                        {/* Title */}
                        <div>
                            <label style={labelStyle}>Title *</label>
                            <input value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} required style={inputStyle} placeholder="Enter news title" />
                        </div>

                        {/* Description */}
                        <div>
                            <label style={labelStyle}>Description</label>
                            <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }} placeholder="Enter news description" />
                        </div>

                        {/* Image URL */}
                        <div>
                            <label style={labelStyle}>Image URL</label>
                            <input value={formData.imageUrl} onChange={(e) => setFormData({ ...formData, imageUrl: e.target.value })} style={inputStyle} placeholder="https://example.com/image.jpg" />
                        </div>

                        {/* Button Row */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={labelStyle}>Button Text</label>
                                <input value={formData.buttonText} onChange={(e) => setFormData({ ...formData, buttonText: e.target.value })} style={inputStyle} placeholder="Learn More" />
                            </div>
                            <div>
                                <label style={labelStyle}>Button Color</label>
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <input type="color" value={formData.buttonColor || '#10b981'} onChange={(e) => setFormData({ ...formData, buttonColor: e.target.value })} style={{ width: '50px', height: '44px', padding: '4px', background: '#0f172a', border: '1px solid #334155', borderRadius: '10px', cursor: 'pointer' }} />
                                    <input value={formData.buttonColor} onChange={(e) => setFormData({ ...formData, buttonColor: e.target.value })} style={{ ...inputStyle, flex: 1 }} placeholder="#10b981" />
                                </div>
                            </div>
                        </div>

                        {/* Navigation URL */}
                        <div>
                            <label style={labelStyle}>Navigation URL</label>
                            <input value={formData.navigationUrl} onChange={(e) => setFormData({ ...formData, navigationUrl: e.target.value })} style={inputStyle} placeholder="/features or https://..." />
                        </div>

                        {/* Position & Active */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                            <div>
                                <label style={labelStyle}>Position</label>
                                <input type="number" min="1" value={formData.position} onChange={(e) => setFormData({ ...formData, position: parseInt(e.target.value) || 1 })} style={inputStyle} />
                            </div>
                            <div>
                                <label style={labelStyle}>Active Status</label>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', height: '44px' }}>
                                    <button type="button" onClick={() => setFormData({ ...formData, isActive: !formData.isActive })} style={{ width: '50px', height: '28px', borderRadius: '14px', border: 'none', background: formData.isActive ? COLORS.success : '#334155', cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
                                        <div style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#fff', position: 'absolute', top: '3px', left: formData.isActive ? '25px' : '3px', transition: 'left 0.2s' }} />
                                    </button>
                                    <span style={{ color: formData.isActive ? COLORS.success : '#64748b', fontSize: '14px', fontWeight: '600' }}>{formData.isActive ? 'Active' : 'Inactive'}</span>
                                </div>
                            </div>
                        </div>

                        {/* Preview */}
                        {formData.imageUrl && (
                            <div>
                                <label style={labelStyle}>Preview</label>
                                <div style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '12px', padding: '16px', display: 'flex', gap: '16px', alignItems: 'center' }}>
                                    <img src={formData.imageUrl} alt="Preview" style={{ width: '80px', height: '80px', objectFit: 'cover', borderRadius: '8px' }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                                    <div style={{ flex: 1 }}>
                                        <div style={{ color: '#f1f5f9', fontSize: '16px', fontWeight: '600', marginBottom: '4px' }}>{formData.title || 'Title'}</div>
                                        <div style={{ color: '#64748b', fontSize: '13px', marginBottom: '8px' }}>{formData.description || 'Description'}</div>
                                        {formData.buttonText && (
                                            <button type="button" style={{ padding: '6px 14px', background: formData.buttonColor || COLORS.primary, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600' }}>{formData.buttonText}</button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Actions */}
                    <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid #334155' }}>
                        <button type="button" onClick={onClose} style={{ padding: '12px 24px', background: '#334155', color: '#f1f5f9', border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                        <button type="submit" disabled={isLoading || !formData.title} style={{ padding: '12px 24px', background: isLoading || !formData.title ? '#334155' : COLORS.primary, color: isLoading || !formData.title ? '#64748b' : '#0f172a', border: 'none', borderRadius: '10px', fontSize: '14px', cursor: isLoading || !formData.title ? 'not-allowed' : 'pointer', fontWeight: '600' }}>{isLoading ? 'Saving...' : (news ? 'Update' : 'Create')}</button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// Delete Confirmation Modal
const DeleteModal = ({ news, onConfirm, onClose, isLoading }: { news: News; onConfirm: () => void; onClose: () => void; isLoading: boolean }) => (
    <div onClick={(e) => e.target === e.currentTarget && onClose()} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '24px' }}>
        <div style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '20px', border: '1px solid #334155', width: '100%', maxWidth: '450px', padding: '32px', textAlign: 'center' }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🗑️</div>
            <h2 style={{ color: '#f1f5f9', fontSize: '20px', fontWeight: '700', marginBottom: '8px' }}>Delete News?</h2>
            <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '24px' }}>Are you sure you want to delete "<span style={{ color: '#f1f5f9' }}>{news.title}</span>"? This action cannot be undone.</p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={onClose} style={{ padding: '12px 24px', background: '#334155', color: '#f1f5f9', border: 'none', borderRadius: '10px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
                <button onClick={onConfirm} disabled={isLoading} style={{ padding: '12px 24px', background: isLoading ? '#334155' : COLORS.danger, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '14px', cursor: isLoading ? 'not-allowed' : 'pointer', fontWeight: '600' }}>{isLoading ? 'Deleting...' : 'Delete'}</button>
            </div>
        </div>
    </div>
);

// Main News Page Component
export default function NewsPage() {
    const [news, setNews] = useState<News[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [showTokenModal, setShowTokenModal] = useState(false);
    const [showFormModal, setShowFormModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedNews, setSelectedNews] = useState<News | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const loadNews = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await fetchAllNews(true);
            setNews(data);
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadNews();
    }, []);

    const showSuccess = (message: string) => {
        setSuccessMessage(message);
        setTimeout(() => setSuccessMessage(null), 3000);
    };

    const handleCreate = () => {
        setSelectedNews(null);
        setShowFormModal(true);
    };

    const handleEdit = (item: News) => {
        setSelectedNews(item);
        setShowFormModal(true);
    };

    const handleDelete = (item: News) => {
        setSelectedNews(item);
        setShowDeleteModal(true);
    };

    const handleSaveNews = async (data: CreateNewsDto | UpdateNewsDto) => {
        try {
            setIsSubmitting(true);
            setError(null);
            if (selectedNews) {
                await updateNews(selectedNews.id, data);
                showSuccess('News updated successfully!');
            } else {
                await createNews(data as CreateNewsDto);
                showSuccess('News created successfully!');
            }
            setShowFormModal(false);
            loadNews();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedNews) return;
        try {
            setIsSubmitting(true);
            setError(null);
            await deleteNews(selectedNews.id);
            showSuccess('News deleted successfully!');
            setShowDeleteModal(false);
            loadNews();
        } catch (err: any) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)', padding: '32px' }}>
            <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
                {/* Header */}
                <div style={{ marginBottom: '32px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
                    <div>
                        <h1 style={{ color: '#f1f5f9', fontSize: '32px', fontWeight: '800', marginBottom: '8px' }}>📰 News Management</h1>
                        <p style={{ color: '#64748b', fontSize: '14px' }}>{news.length} news item{news.length !== 1 ? 's' : ''}</p>
                    </div>
                    <div style={{ display: 'flex', gap: '12px' }}>
                        <button onClick={() => setShowTokenModal(true)} style={{ padding: '12px 20px', background: '#334155', color: '#f1f5f9', border: 'none', borderRadius: '12px', fontSize: '14px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            🔐 Set Token
                        </button>
                        <button onClick={handleCreate} style={{ padding: '12px 20px', background: COLORS.primary, color: '#0f172a', border: 'none', borderRadius: '12px', fontSize: '14px', cursor: 'pointer', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            ➕ Add News
                        </button>
                    </div>
                </div>

                {/* Success Message */}
                {successMessage && (
                    <div style={{ background: '#064e3b', border: '1px solid #10b981', padding: '16px 20px', borderRadius: '12px', color: '#10b981', fontSize: '14px', marginBottom: '24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                        ✅ {successMessage}
                    </div>
                )}

                {/* Error Message */}
                {error && (
                    <div style={{ background: '#7f1d1d', border: '1px solid #ef4444', padding: '16px 20px', borderRadius: '12px', color: '#fca5a5', fontSize: '14px', marginBottom: '24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>❌ {error}</span>
                        <button onClick={() => setError(null)} style={{ background: 'transparent', border: 'none', color: '#fca5a5', fontSize: '18px', cursor: 'pointer' }}>×</button>
                    </div>
                )}

                {/* Content */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#64748b' }}>⏳ Loading news...</div>
                ) : news.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px' }}>
                        <div style={{ fontSize: '48px', marginBottom: '16px' }}>📭</div>
                        <div style={{ color: '#64748b', fontSize: '16px', marginBottom: '24px' }}>No news items yet</div>
                        <button onClick={handleCreate} style={{ padding: '12px 24px', background: COLORS.primary, color: '#0f172a', border: 'none', borderRadius: '12px', fontSize: '14px', cursor: 'pointer', fontWeight: '600' }}>Create your first news item</button>
                    </div>
                ) : (
                    <div style={{ display: 'grid', gap: '16px' }}>
                        {news.map((item) => (
                            <div key={item.id} style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)', borderRadius: '16px', border: '1px solid #334155', padding: '20px', display: 'flex', gap: '20px', alignItems: 'center' }}>
                                {/* Image */}
                                {item.imageUrl ? (
                                    <img src={item.imageUrl} alt={item.title} style={{ width: '100px', height: '100px', objectFit: 'cover', borderRadius: '12px', flexShrink: 0 }} onError={(e) => (e.currentTarget.style.display = 'none')} />
                                ) : (
                                    <div style={{ width: '100px', height: '100px', background: '#334155', borderRadius: '12px', display: 'flex', justifyContent: 'center', alignItems: 'center', fontSize: '32px', flexShrink: 0 }}>📰</div>
                                )}

                                {/* Content */}
                                <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                                        <span style={{ background: item.isActive ? COLORS.success : '#334155', color: '#fff', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>{item.isActive ? 'Active' : 'Inactive'}</span>
                                        <span style={{ background: '#334155', color: '#94a3b8', padding: '4px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: '600' }}>Position: {item.position}</span>
                                    </div>
                                    <h3 style={{ color: '#f1f5f9', fontSize: '18px', fontWeight: '700', marginBottom: '6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.title}</h3>
                                    <p style={{ color: '#64748b', fontSize: '14px', marginBottom: '12px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.description || 'No description'}</p>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                                        {item.buttonText && (
                                            <button style={{ padding: '6px 14px', background: item.buttonColor || COLORS.primary, color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'default' }}>{item.buttonText}</button>
                                        )}
                                        <span style={{ color: '#64748b', fontSize: '12px' }}>Created: {format(new Date(item.createdAt), 'MMM dd, yyyy')}</span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
                                    <button onClick={() => handleEdit(item)} style={{ padding: '10px 16px', background: COLORS.secondary, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>✏️ Edit</button>
                                    <button onClick={() => handleDelete(item)} style={{ padding: '10px 16px', background: COLORS.danger, color: '#fff', border: 'none', borderRadius: '10px', fontSize: '13px', cursor: 'pointer', fontWeight: '600' }}>🗑️ Delete</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Modals */}
            {showTokenModal && <TokenModal onSave={() => setShowTokenModal(false)} onClose={() => setShowTokenModal(false)} />}
            {showFormModal && <NewsFormModal news={selectedNews} onSave={handleSaveNews} onClose={() => setShowFormModal(false)} isLoading={isSubmitting} />}
            {showDeleteModal && selectedNews && <DeleteModal news={selectedNews} onConfirm={handleConfirmDelete} onClose={() => setShowDeleteModal(false)} isLoading={isSubmitting} />}
        </div>
    );
}
